import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ja';

import MoveTop from '../../CommonParts';
import customReserveForm from '../../api/reserve/customReserveForm';
import changeConfirmation from '../../api/reserveChange/changeConfirmation';
import configMastaApi from '../../api/masta/configMasta';
import { useNavigateWithId } from '../../customHooks';
import {
  saveFormAnswers,
  saveUserInfo,
  saveSendType,
} from '../../redux_toolkit/reserveChangeSlice';
import TopicPath from '../../components/TopicPath';
import { ReserveHeader } from '../../components/Header';
import { Footer } from '../../components/Footer';
import ChangeSelectionSummary from '../../components/ChangeSelectionSummary';
// 既存の予約フォーム用関数コンポーネントを利用（CustomizeReserveForm.jsx と共通化）
import {
  MakeText,
  MakeTextArea,
  MakeDate,
  MakeRadioButton,
  MakeCheckBox,
  MakeSelectBox,
  MakeInt,
  MakeIntEng,
  MakeUrlItem,
  TelNumBox,
} from '../reserve/CustomizeReserveForm';
import {
  INPUT_TYPE_TEXT,
  INPUT_TEXTAREA_FLAG,
  INPUT_DATE_FLAG,
  INPUT_EXP_FLAG,
  INPUT_RADIO_FLAG,
  INPUT_CHECK_FLAG,
  INPUT_SELECT_FLAG,
  INPUT_TELLNUM_FLAG,
  INPUT_ENGMATH_FLAG,
  INPUT_MATHONLY_FLAG,
  INPUT_URL_FLAG,
  INPUT_TYPE_NAME,
  INPUT_TYPE_ADDRESS,
  INPUT_TYPE_BIRTHDAY,
  INPUT_TYPE_PHONE,
  MAX_CHARACTER_LIMIT,
  CANCEL_CHANGE_ANNOUNCE,
} from '../../constants';

dayjs.extend(utc);
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.locale('ja');

// 個人情報フィールドのinputTypeリスト（ChangeFormでは表示しない）
const PERSONAL_INFO_TYPES = [
  INPUT_TYPE_NAME,
  INPUT_TYPE_ADDRESS,
  INPUT_TYPE_BIRTHDAY,
  INPUT_TYPE_PHONE,
];

const ChangeForm = function () {
  const navigate = useNavigateWithId();
  const dispatch = useDispatch();
  const location = useLocation();
  const handleError = useErrorHandler();

  const lgCd = useSelector((state) => state.reserveChange.lgCd);
  const rsvSetId = useSelector((state) => state.reserveChange.rsvSetId);
  const receptionId = useSelector((state) => state.reserveChange.receptionId);
  const businessChangeMode = useSelector((state) => state.reserveChange.businessChangeMode);
  const businessChanged = useSelector((state) => state.reserveChange.businessChanged);
  // モード1かつ用件変更あり → CustomizeReserveForm と同じフル表示
  const showFullForm = businessChangeMode === 1 && businessChanged;
  const storeNumOfAttendees = useSelector((state) => state.reserveChange.numOfAttendees);
  const storeUseHome = useSelector((state) => state.reserveChange.useHome);
  const storeOfRecItem = useSelector((state) => state.reserveChange.recItem);
  const storeNote = useSelector((state) => state.reserveChange.note);
  const fromChangeForm = useSelector((state) => state.reserveChange.fromChangeForm);
  // 確認画面から戻った時に answerList を復元するため Redux から読む
  const storeAnswerList = useSelector((state) => state.reserveChange.answerList);
  // モード1 で「前に戻る」して戻ってきたときに編集済みの個人情報を復元するため Redux から読む
  const storeUserName = useSelector((state) => state.reserveChange.userName);
  const storeAddress = useSelector((state) => state.reserveChange.address);
  const storeBirthday = useSelector((state) => state.reserveChange.birthday);
  const storeTelNum = useSelector((state) => state.reserveChange.telNum);
  const storeMailAddress = useSelector((state) => state.reserveChange.mailAddress);
  const storeSendType = useSelector((state) => state.reserveChange.sendType);
  const rsvConfig = useSelector((state) => state.rsvConfig);
  const rsvFlag = useSelector((state) => state.reserveChange.rsvFlag);
  const { topicPath = [], maxAttendees = 0 } = rsvConfig;

  // ChangeTime から受け取った日時情報
  const newDate = location.state?.date ?? '';
  const newDayOfWeek = location.state?.newDayOfWeek ?? '';
  const sameDay = location.state?.sameDay ?? false;
  const sameTime = location.state?.sameTime ?? false;

  const [formSetting, setFormSetting] = useState('');
  const [answerList, setAnswerList] = useState([]);
  // showFullForm かつ初回訪問時は前回予約の値を引き継がず 0 で初期化する
  const [numOfAttendees, setNumOfAttendees] = useState(
    showFullForm && !fromChangeForm ? 0 : storeNumOfAttendees
  );
  const [numOfAttendeesOptions, setNumOfAttendeesOptions] = useState([]);
  const [pageName, setPageName] = useState('');
  const [err, setError] = useState([]);
  // showFullForm かつ初回訪問時は前回予約の備考を引き継がず空で初期化する
  const [note, setNote] = useState(showFullForm && !fromChangeForm ? '' : storeNote);
  const [remarksColumnPlaceholder, setRemarksColumnPlaceholder] = useState('');
  const [remarksColumnLimit, setRemarksColumnLimit] = useState(0);

  // モード1 専用: 個人情報フィールドの state
  const storeBirthdayObj = storeBirthday ? dayjs(storeBirthday) : null;
  const [name, setName] = useState(fromChangeForm ? storeUserName : '');
  const [address, setAddress] = useState(fromChangeForm ? storeAddress : '');
  const [birthYear, setBirthYear] = useState(
    fromChangeForm && storeBirthdayObj ? storeBirthdayObj.format('YYYY') : ''
  );
  const [birthMonth, setBirthMonth] = useState(
    fromChangeForm && storeBirthdayObj ? storeBirthdayObj.format('MM') : ''
  );
  const [birthDate, setBirthDate] = useState(
    fromChangeForm && storeBirthdayObj ? storeBirthdayObj.format('DD') : ''
  );
  const [birthday, setBirthday] = useState(fromChangeForm ? storeBirthday : '');
  const [telNum, setTelNum] = useState(fromChangeForm ? storeTelNum : '');
  const [mailAddress, setMailAddress] = useState(fromChangeForm ? storeMailAddress : '');
  // 確認画面から「前に戻る」場合は mailAddress と同じ値で初期化（再入力不要にする）
  const [confMailAddress, setConfMailAddress] = useState(fromChangeForm ? storeMailAddress : '');
  const [sendType, setSendType] = useState(fromChangeForm ? storeSendType : 1);
  const [smsEnable, setSmsEnable] = useState(false);
  // showFullForm 時の SMS セクションで使う送信元メールアドレス
  const [fromMailAddress, setFromMailAddress] = useState('xxx@xxxxxx.ne.jp');
  const [baseName, setBaseName] = useState('');

  // 生年月日入力欄の ref（InputUserInfo.jsx と同様）
  const yearRef = useRef(null);
  const monthRef = useRef(null);
  const dateRef = useRef(null);

  // パンくずリストからページ名を取得
  const getPageNameFromTopicPath = (topicPathConf) => {
    const locationPath = window.location.pathname;
    const tmpPath1 = locationPath.replace('/reserve/', '');
    const tmpPath2 = tmpPath1.replace('/test', '');
    const currentPath = tmpPath2.replace('/demo', '');

    for (let i = 0; i < topicPathConf.length; i += 1) {
      const { url = '', scnName: tmpScnName = '' } = topicPathConf[i];
      if (currentPath === url) {
        setPageName(tmpScnName);
        break;
      }
    }
  };

  // 同席者数の選択肢を作成
  const createOptions = () => {
    const options = [];
    for (let i = 0; i <= maxAttendees; i += 1) {
      const label = i === 0 ? '無し' : `${i}人`;
      options.push(
        <option key={label} value={i}>
          {label}
        </option>
      );
    }
    setNumOfAttendeesOptions(options);
  };

  // 予約受付設定を取得（備考欄のプレースホルダーと文字数上限、smsEnable）
  const getReserveSetting = async () => {
    const result = await customReserveForm.searchReserveSetting(rsvSetId).catch((err) => {
      window.alert('予約受付設定の取得に失敗しました。');
      handleError(err);
    });
    if (!result) return;
    const { remarksColumnPlaceholder, remarksColumnLimit, smsEnable: smsFl = false } = result;
    setRemarksColumnPlaceholder(remarksColumnPlaceholder);
    setRemarksColumnLimit(remarksColumnLimit);
    setSmsEnable(smsFl);
  };

  // フォーム設定を取得し、answerList を初期化
  const loadFormSetting = async () => {
    // 用件あれば予約フォームIDのまま検索。用件変わった場合、その用件で予約フォームを取得。
    const { formSetId = '' } = storeOfRecItem[0];

    // getGroupId 失敗時は null を返して早期リターン（undefined のまま破壊代入するとTypeError）
    const groupIdResult = await customReserveForm.getGroupId(rsvSetId).catch((err) => {
      window.alert('グループ情報の取得に失敗しました。');
      handleError(err);
      return null;
    });
    if (!groupIdResult) return;
    // data が gettingGroupId として getFormSetting の第2引数に渡す値
    const { data } = groupIdResult;

    // getFormSetting 失敗時も null を返して早期リターン
    const setting = await customReserveForm.getFormSetting(lgCd, data, formSetId).catch((err) => {
      window.alert('予約フォーム設定の取得に失敗しました。');
      handleError(err);
      return null;
    });
    const { alternative_name: alterNativeName = '' } = setting;
    setFormSetting(setting);
    setBaseName(alterNativeName);
    if (!setting) return;
    const { input_info: inputInfo = [] } = setting;

    // Mode②（businessChangeMode === 2）の場合は前回の回答を取得して初期値にする
    // Mode①（businessChangeMode === 1）の場合も個人情報を含む前回値を取得する
    let previousAnswers = [];
    if (businessChangeMode === 2 || businessChangeMode === 1) {
      const {
        answerList: prevList = [],
        numOfAttendees: prevNum,
        note: prevNote,
        userName: prevUserName,
        address: prevAddress,
        birthday: prevBirthday,
        telNum: prevTelNum,
        mailAddress: prevMailAddress,
        sendType: prevSendType,
      } = await changeConfirmation.getPreviousAnswers(receptionId).catch((err) => {
        window.alert('前回の回答の取得に失敗しました。');
        handleError(err);
        return { answerList: [] };
      });

      previousAnswers = [...prevList];

      // 初回訪問時のみ元予約の値をセット（確認画面から「前に戻る」の場合は編集済み値を保持）
      if (!fromChangeForm) {
        // showFullForm（用件変更あり）の場合は同席者数・備考を前回から引き継がない
        // showFullForm（用件変更あり）の場合、または施設/電話利用の場合は同席者数を引き継がない
        if (!showFullForm) {
          if (prevNum !== undefined && storeUseHome) setNumOfAttendees(prevNum);
          if (prevNote !== undefined) setNote(prevNote);
        }

        // モード1のとき個人情報も前回値でプリセット
        if (businessChangeMode === 1) {
          if (prevUserName !== undefined) setName(prevUserName);
          if (prevAddress !== undefined) setAddress(prevAddress);
          if (prevBirthday !== undefined && prevBirthday !== '') {
            // birthday は Date ISO 文字列 or "YYYY-MM-DD" で返ってくるので dayjs でパース
            const parsedBirthday = dayjs(prevBirthday);
            if (parsedBirthday.isValid()) {
              setBirthYear(parsedBirthday.format('YYYY'));
              setBirthMonth(parsedBirthday.format('MM'));
              setBirthDate(parsedBirthday.format('DD'));
              setBirthday(parsedBirthday.format('YYYY-MM-DD'));
            }
          }
          // showFullForm（Mode①かつ用件変更あり）の場合は電話番号を引き継がない
          // （SMS送信用の電話番号欄を未入力状態にするため。メールアドレス・備考等と同じ挙動）
          if (prevTelNum !== undefined && !showFullForm) setTelNum(prevTelNum);
          // showFullForm（Mode①かつ用件変更あり）の場合はメールアドレスを引き継がない
          if (prevMailAddress !== undefined && !showFullForm) {
            setMailAddress(prevMailAddress);
            setConfMailAddress(prevMailAddress); // 確認欄も自動セット
          }
          // showFullForm（用件変更を伴う予約変更）の場合は送信方法を引き継がず、
          // 初期値メール(1)のままにする（電話番号・メールアドレスと同じ挙動）
          if (prevSendType !== undefined && !showFullForm) setSendType(prevSendType);
        }
      }
    }

    // answerList を初期化
    const startValue = [];
    for (let i = 0; i < inputInfo.length; i += 1) {
      const { title, input_type: inputType = 1, require = true, selection = [] } = inputInfo[i];

      // 確認画面から戻った場合（showFullForm時）はReduxに保存済みの入力内容を復元する
      // Mode② または Mode①用件変更なし の場合のみ前回API回答を引き継ぐ
      // showFullForm（Mode①かつ用件変更あり）の場合は要件が変わっているため前回のフォーム回答を引き継がない
      const prevAnswer = previousAnswers[i];
      // fromChangeForm=true であれば全モードで Redux の保存データを優先して復元する
      // （showFullForm 限定だと モード2・モード1用件変更なし で前に戻る時に消えるバグを防ぐため）
      const savedAnswer = fromChangeForm ? storeAnswerList[i] : undefined;
      if (savedAnswer) {
        startValue.push(savedAnswer);
      } else if (
        prevAnswer &&
        (businessChangeMode === 2 || (businessChangeMode === 1 && !showFullForm))
      ) {
        // フォームが変更されていても input_type が同じ場合のみ前回値を引き継ぐ
        // （input_type が異なる場合は項目の性質が変わっているためデフォルト値を使う）
        if (prevAnswer.inputType !== inputType) {
          // input_type が異なる → デフォルト値
          if (
            inputType === INPUT_RADIO_FLAG ||
            inputType === INPUT_CHECK_FLAG ||
            inputType === INPUT_SELECT_FLAG
          ) {
            startValue.push({ question: title, inputType, answer: [], require });
          } else {
            startValue.push({ question: title, inputType, answer: [''], require });
          }
        } else if (
          inputType === INPUT_RADIO_FLAG ||
          inputType === INPUT_CHECK_FLAG ||
          inputType === INPUT_SELECT_FLAG
        ) {
          // ラジオ・チェック・セレクトは新フォームの選択肢に存在する値のみ引き継ぐ
          // （フォーム変更で選択肢が削除・変更されていても安全に引き継げる）
          const validAnswers = prevAnswer.answer.filter((ans) => selection.includes(ans));
          startValue.push({ question: title, inputType, answer: validAnswers, require });
        } else {
          // テキスト系（テキスト・テキストエリア・日付・数字・英数字・電話番号）はそのまま引き継ぐ
          startValue.push({ question: title, inputType, answer: prevAnswer.answer, require });
        }
      } else if (
        inputType === INPUT_RADIO_FLAG ||
        inputType === INPUT_CHECK_FLAG ||
        inputType === INPUT_SELECT_FLAG
      ) {
        startValue.push({ question: title, inputType, answer: [], require });
      } else {
        startValue.push({ question: title, inputType, answer: [''], require });
      }
    }

    setAnswerList(startValue);
    setFormSetting(setting);
  };

  // 生年月日フィールドの useEffect（InputUserInfo.jsx と同様の挙動）
  useEffect(() => {
    if (businessChangeMode !== 1) return;
    const birthDayStr = `${birthYear}-${birthMonth}-${birthDate}`;
    if (birthDayStr.length === 10) {
      const dayjsObj = dayjs(birthDayStr);
      if (!dayjsObj.isValid()) {
        setBirthYear('');
        setBirthMonth('');
        setBirthDate('');
        setBirthday('');
        return;
      }
      const fixedYear = dayjsObj.get('year');
      const fixedMonth = dayjsObj.get('month') + 1;
      const fixedDate = dayjsObj.get('date');
      if (parseInt(birthYear, 10) !== parseInt(fixedYear, 10)) {
        setBirthYear(fixedYear.toString());
      }
      if (parseInt(birthMonth, 10) !== parseInt(fixedMonth, 10)) {
        setBirthMonth(fixedMonth.toString().padStart(2, '0'));
      }
      if (parseInt(birthDate, 10) !== parseInt(fixedDate, 10)) {
        setBirthDate(fixedDate.toString().padStart(2, '0'));
      }
      setBirthday(dayjsObj.format('YYYY[-]MM[-]DD'));
    } else if (birthday !== '') {
      setBirthday('');
    }
  }, [birthYear, birthMonth, birthDate]);

  // 年4桁入力で月フィールドへフォーカス移動
  useEffect(() => {
    if (businessChangeMode !== 1) return;
    if (yearRef.current && birthYear.length === 4) monthRef.current?.focus();
  }, [birthYear]);

  // 月2桁入力で日フィールドへ、0桁になったら年フィールドへフォーカス移動
  useEffect(() => {
    if (businessChangeMode !== 1) return;
    if (birthMonth.length === 2) dateRef.current?.focus();
    else if (birthMonth.length === 0) yearRef.current?.focus();
  }, [birthMonth]);

  // 日0桁になったら月フィールドへフォーカス移動
  useEffect(() => {
    if (businessChangeMode !== 1) return;
    if (birthDate.length === 0) monthRef.current?.focus();
  }, [birthDate]);

  // 最初に描画する（CustomizeReserveForm.jsx に合わせ useEffect を使用）
  useEffect(() => {
    getPageNameFromTopicPath(topicPath);
    createOptions();
    loadFormSetting();
    getReserveSetting();
    // showFullForm（モード1かつ用件変更あり）の場合は送信元メールアドレスを取得
    if (showFullForm) {
      configMastaApi
        .findByConfCd('rsv_from_mailaddress')
        .then((conf) => {
          if (conf?.value) setFromMailAddress(conf.value);
        })
        .catch(() => { });
    }
  }, []);

  // 回答の更新
  const changeSetter = (obj, num) => {
    const newAnswerList = [...answerList];
    newAnswerList[num] = obj;
    setAnswerList(newAnswerList);
  };

  // バリデーション（CustomizeReserveForm.jsx の customValidate に準拠）
  // display: フォーム設定の display フラグ（false の項目はバリデーションをスキップ）
  const customValidate = (answerItem, characterLimit, display = true) => {
    if (!answerItem) return;
    const { question = '', answer = [], require = true, inputType = 1 } = answerItem;

    // showFullForm でない場合は個人情報フィールドをスキップ（既存の動作を維持）
    if (!showFullForm && PERSONAL_INFO_TYPES.includes(inputType)) return;

    const inputMessage = `項目:「${question}」を入力してください。\n`;
    const selectMessage = `項目:「${question}」を選択してください。\n`;
    const characterLimitMessage = `項目:「${question}」は${characterLimit}文字以内で入力してください。\n`;

    // showFullForm 時: 個人情報フィールドのバリデーション（CustomizeReserveForm.jsx と同等）
    if (showFullForm) {
      // 氏名
      if (inputType === INPUT_TYPE_NAME && display) {
        if (require && answer[0].trim() === '') err.push(inputMessage);
        if (answer[0].length > characterLimit && characterLimit !== 0)
          err.push(characterLimitMessage);
        if (
          answer[0].length !== 0 &&
          !/^[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}a-zA-Z々ー・＝－ 　]+$/u.test(
            answer[0]
          )
        ) {
          err.push(
            `項目:「${question}」は、ひらがな、全角カタカナ、漢字、半角英語、・＝ーのみで入力してください。\n`
          );
        }
      }
      // 住所
      if (inputType === INPUT_TYPE_ADDRESS && display) {
        if (require && answer[0].trim() === '') err.push(inputMessage);
        if (answer[0].length > characterLimit && characterLimit !== 0)
          err.push(characterLimitMessage);
      }
      // 生年月日
      if (inputType === INPUT_TYPE_BIRTHDAY && display) {
        if (require || answer[0].length !== 0) {
          const dateArray = answer[0].split('-');
          if (!dateArray[0] || !dateArray[1] || !dateArray[2]) {
            err.push(`項目:「${question}」は年・月・日を全て入力してください。\n`);
          } else if (new Date(answer[0]).toString() === 'Invalid Date') {
            err.push(`項目:「${question}」を正しく入力してください。\n`);
          } else if (
            dayjs(answer[0]).isBefore(dayjs('1900-01-01')) ||
            dayjs(answer[0]).isAfter(dayjs('2100-12-31'))
          ) {
            err.push(
              `項目:「${question}」は1900年1月1日から2100年12月31日の範囲で入力してください。\n`
            );
          }
        }
      }
      // 電話番号（INPUT_TYPE_PHONE）
      if (inputType === INPUT_TYPE_PHONE && display === true) {
        if (require && answer[0].trim() === '') err.push(inputMessage);
        if (answer[0] !== '') {
          const telRegex = /^[0-9]{1,5}-?[0-9]{1,5}-?[0-9]{1,5}$/;
          if (answer[0].length > 13)
            err.push(`項目:「${question}」は13文字以内で入力してください。\n`);
          if (!telRegex.test(answer[0]))
            err.push(`項目:「${question}」の形式が正しくありません。\n`);
        }
      }
      // 個人情報フィールドはここで処理済みなので以降のチェックを抜ける
      if (PERSONAL_INFO_TYPES.includes(inputType)) {
        setError(...err);
        return;
      }
    }

    // テキスト
    if (inputType === INPUT_TYPE_TEXT) {
      if (require === true && answer[0].trim() === '') err.push(inputMessage);
      if (answer[0].length > characterLimit && characterLimit !== 0)
        err.push(characterLimitMessage);
    }
    // テキストエリア
    if (inputType === INPUT_TEXTAREA_FLAG) {
      if (require === true && answer[0].trim() === '') err.push(inputMessage);
      if (answer[0].length > characterLimit && characterLimit !== 0)
        err.push(characterLimitMessage);
    }
    // 日付
    if (inputType === INPUT_DATE_FLAG) {
      if (require || answer[0].length !== 0) {
        const dateArray = answer[0].split('-');
        if (!dateArray[0] || !dateArray[1] || !dateArray[2]) {
          err.push(`項目:「${question}」は年・月・日を全て入力してください。\n`);
        } else if (new Date(answer[0]).toString() === 'Invalid Date') {
          err.push(`項目:「${question}」を正しく入力してください。\n`);
        } else if (
          dayjs(answer[0]).isBefore(dayjs('1900-01-01')) ||
          dayjs(answer[0]).isAfter(dayjs('2100-12-31'))
        ) {
          err.push(
            `項目:「${question}」は1900年1月1日から2100年12月31日の範囲で入力してください。\n`
          );
        }
      }
    }
    // 電話番号
    if (inputType === INPUT_TELLNUM_FLAG) {
      if (require && answer[0].trim() === '') err.push(inputMessage);
      if (answer[0] !== '') {
        const telRegex = /^[0-9]{1,5}-?[0-9]{1,5}-?[0-9]{1,5}$/;
        if (answer[0].length > 13)
          err.push(`項目:「${question}」は13文字以内で入力してください。\n`);
        if (!telRegex.test(answer[0])) err.push(`項目:「${question}」の形式が正しくありません。\n`);
      }
    }
    // ラジオボタン
    if (inputType === INPUT_RADIO_FLAG) {
      if (answer.length === 0 && require === true) err.push(selectMessage);
      if (answer[0] === '') err.push(`項目:「${question}」が正しく選択されていません。\n`);
      if (answer.length > 1) err.push(`項目:「${question}」の選択は1つまでです。\n`);
    }
    // チェックボックス
    if (inputType === INPUT_CHECK_FLAG) {
      if (require === true && answer.length === 0) err.push(selectMessage);
      else if (answer.includes('') === true)
        err.push(`項目:「${question}」が正しく選択されていません。\n`);
    }
    // セレクトボックス
    if (inputType === INPUT_SELECT_FLAG) {
      if (answer.length === 0 && require === true) err.push(selectMessage);
      if (answer[0] === '') err.push(`項目:「${question}」が正しく選択されていません。\n`);
      if (answer.length > 1) err.push(`項目:「${question}」の選択は1つまでです。\n`);
    }
    // 数字のみ
    if (inputType === INPUT_MATHONLY_FLAG) {
      if (require === true && answer[0].trim() === '') err.push(inputMessage);
      if (answer[0].length > characterLimit && characterLimit !== 0)
        err.push(`項目:「${question}」は${characterLimit}桁以内で入力してください。\n`);
      if (!/^[0-9]+$/.test(answer[0]) && answer[0] !== '')
        err.push(`項目:「${question}」は半角数字で入力してください。\n`);
      if (answer[0].length > MAX_CHARACTER_LIMIT)
        err.push(`項目:「${question}」の入力できる最大文字数を超えています。\n`);
    }
    // 英数字
    if (inputType === INPUT_ENGMATH_FLAG) {
      if (require === true && answer[0].trim() === '') err.push(inputMessage);
      if (answer[0].length > characterLimit && characterLimit !== 0)
        err.push(characterLimitMessage);
      if (answer[0].length > MAX_CHARACTER_LIMIT)
        err.push(`項目:「${question}」の入力できる最大文字数を超えています。\n`);
      if (!/^[a-zA-Z0-9]+$/.test(answer[0]) && answer[0] !== '')
        err.push(`項目:「${question}」は半角英数字で入力してください。\n`);
    }

    setError(...err);
  };

  // showFullForm 専用: SMS/メールアドレスセクションのバリデーション（CustomizeReserveForm.jsx の validate() と同等）
  const validateEmailSms = () => {
    // SMS 利用時は携帯番号チェック（telNum state は「電話番号を転記」ボタンで設定）
    const mobileRegex = new RegExp(/^0[789]0-?[0-9]{4}-?[0-9]{4}$/);
    if ((sendType === 2 || sendType === 3) && !mobileRegex.test(telNum)) {
      err.push('SMSを利用する場合は携帯電話の番号を入力してください。\n');
    }

    // メールアドレスが区切り文字でつながっていないかを確認
    const separatorPattern = /[,:;、：；\s\u3000]/;
    if (separatorPattern.test(mailAddress)) {
      err.push('複数のメールアドレスは入力できません。区切り文字（, や ;）を削除し、1件のみ入力してください。\n');
    }
    // メールアドレスのチェック
    if (!mailAddress.match(/\S/g) && (sendType === 1 || sendType === 3)) {
      err.push('メールアドレスを入力してください。\n');
    } else if (mailAddress.length > 256) {
      err.push('メールアドレスは256文字以内で入力してください。\n');
    } else {
      const test = new RegExp(/^[a-zA-Z0-9!-/:-@¥[-`{-~]*$/);
      if (
        (!mailAddress.includes('@') || !mailAddress.includes('.') || !test.test(mailAddress)) &&
        (sendType === 1 || sendType === 3)
      ) {
        err.push('メールアドレスの形式が正しくありません。\n');
      }
    }

    if (!confMailAddress.match(/\S/g) && (sendType === 1 || sendType === 3)) {
      err.push('メールアドレス(確認)を入力してください。\n');
    }

    if (
      mailAddress.match(/\S/g) &&
      confMailAddress.match(/\S/g) &&
      mailAddress !== confMailAddress
    ) {
      err.push('メールアドレスが確認用メールアドレスと異なります。\n');
    }
  };

  // モード1 専用: 個人情報のバリデーション（InputUserInfo.jsx の validate() と同等）
  const validatePersonalInfo = () => {
    let message = '';

    // 氏名
    if (!name.match(/\S/g)) {
      message += '氏名を入力してください。\n';
    } else if (name.length > 30) {
      message += '氏名は30文字以内で入力してください。\n';
    } else if (name.match(/[!"#$%&'()*+\-.,/:;<=>?@[\\\]^_`{|}~]/g)) {
      message += '氏名に記号が含まれています。\n';
    }

    // 住所（文字数上限はフォーム設定から取得）
    const { input_info: addressInputInfo = [] } = formSetting || {};
    const addressItem = addressInputInfo.find((item) => item.input_type === INPUT_TYPE_ADDRESS);
    const addressCharLimit = addressItem?.character_limit ?? 120;
    if (addressCharLimit !== 0 && address.length > addressCharLimit) {
      message += `住所は${addressCharLimit}文字以内で入力してください。\n`;
    }

    // 生年月日
    if (birthYear || birthMonth || birthDate) {
      if (!birthYear || !birthMonth || !birthDate) {
        message += '生年月日は年・月・日を全て入力してください。\n';
      } else if (
        new Date(`${birthYear}-${birthMonth}-${birthDate}`).toString() === 'Invalid Date'
      ) {
        message += '生年月日を正しく入力してください。\n';
      } else if (
        dayjs(`${birthYear}-${birthMonth}-${birthDate}`).isBefore(dayjs('1900-01-01')) ||
        dayjs(`${birthYear}-${birthMonth}-${birthDate}`).isAfter(dayjs('2100-12-31'))
      ) {
        message += '生年月日は1900年1月1日から2100年12月31日の範囲で入力してください。\n';
      }
    }

    // 電話番号
    if (!telNum.match(/\S/g)) {
      message += '電話番号を入力してください。\n';
    } else {
      const telRegex = new RegExp('^[0-9]{1,5}-?[0-9]{1,5}-?[0-9]{1,5}$');
      if (telNum.length > 13) {
        message += '電話番号は13文字以内で入力してください。\n';
      } else if (!telRegex.test(telNum)) {
        message += '電話番号の形式が正しくありません。\n';
      }
    }

    // メールアドレス
    if (!mailAddress.match(/\S/g) && (sendType === 1 || sendType === 3)) {
      message += 'メールアドレスを入力してください。\n';
    } else if (mailAddress.length > 256) {
      message += 'メールアドレスは256文字以内で入力してください。\n';
    } else {
      const test = new RegExp(/^[a-zA-Z0-9!-/:-@¥[-`{-~]*$/);
      if (
        (!mailAddress.includes('@') || !mailAddress.includes('.') || !test.test(mailAddress)) &&
        (sendType === 1 || sendType === 3)
      ) {
        message += 'メールアドレスの形式が正しくありません。\n';
      }
    }

    // メールアドレス確認
    if (!confMailAddress.match(/\S/g) && (sendType === 1 || sendType === 3)) {
      message += 'メールアドレス(確認)を入力してください。\n';
    }
    if (
      mailAddress.match(/\S/g) &&
      confMailAddress.match(/\S/g) &&
      mailAddress !== confMailAddress
    ) {
      message += 'メールアドレスが確認用メールアドレスと異なります。\n';
    }

    // SMS 利用時は携帯番号チェック
    const mobileRegex = new RegExp(/^0[789]0-?[0-9]{4}-?[0-9]{4}$/);
    if ((sendType === 2 || sendType === 3) && !mobileRegex.test(telNum)) {
      message += 'SMSを利用する場合は携帯電話の番号を入力してください。\n';
    }

    return message;
  };

  const onClickNext = () => {
    const { input_info: inputInfo = [] } = formSetting || {};
    for (let i = 0; i < inputInfo.length; i += 1) {
      // display も取得して customValidate に渡す（個人情報フィールドの display=false 対応）
      const { character_limit: characterLimit = MAX_CHARACTER_LIMIT, display = true } =
        inputInfo[i];
      customValidate(answerList[i], characterLimit, display);
    }

    // 備考の文字数チェック
    if (remarksColumnLimit > 0 && note && note.length > remarksColumnLimit) {
      err.push(`備考は${remarksColumnLimit}文字以内で入力してください。\n`);
    }

    if (showFullForm) {
      // showFullForm: SMS/メールアドレスセクションのバリデーション
      // （個人情報は customValidate 側で処理済み）
      validateEmailSms();
    } else if (businessChangeMode === 1) {
      // モード1かつ用件変更なし: 個人情報のバリデーション
      const personalMsg = validatePersonalInfo();
      if (personalMsg) err.push(personalMsg);
    }

    if (err.length !== 0) {
      window.alert(err.join(''));
      setError([]);
      return;
    }

    if (showFullForm) {
      // showFullForm: answerList から個人情報を抽出して Redux に保存
      let extractedName = '';
      let extractedAddress = '';
      let extractedBirthday = '';
      let extractedTelNumFromPhone = '';
      let extractedTelNumFromFlag = '';
      for (let i = 0; i < inputInfo.length; i += 1) {
        const { input_type: inputType } = inputInfo[i];
        const ans = answerList[i]?.answer?.[0] ?? '';
        if (inputType === INPUT_TYPE_NAME) extractedName = ans;
        if (inputType === INPUT_TYPE_ADDRESS) extractedAddress = ans;
        if (inputType === INPUT_TYPE_BIRTHDAY) extractedBirthday = ans;
        if (inputType === INPUT_TYPE_PHONE) extractedTelNumFromPhone = ans;
        if (inputType === INPUT_TELLNUM_FLAG) extractedTelNumFromFlag = ans;
      }
      // 1004（INPUT_TYPE_PHONE）を優先し、存在しない場合のみ8（INPUT_TELLNUM_FLAG）を採用
      const extractedTelNum = extractedTelNumFromPhone || extractedTelNumFromFlag;
      dispatch(
        saveUserInfo({
          userName: extractedName,
          address: extractedAddress,
          birthday: extractedBirthday,
          telNum: extractedTelNum,
          mailAddress,
          baseName,
        })
      );
      dispatch(saveSendType({ sendType }));
    } else if (businessChangeMode === 1) {
      // モード1かつ用件変更なし: 既存の個人情報を Redux に保存
      dispatch(saveUserInfo({ userName: name, address, birthday, telNum, mailAddress }));
      dispatch(saveSendType({ sendType }));
    }

    // フォーム回答を Redux に保存し、タイミング情報のみ state で引き継ぐ
    dispatch(saveFormAnswers({ answerList, numOfAttendees, fromChangeForm: true, note }));
    navigate('/change/confirmation', {
      state: { ...location.state },
    });
  };

  const onClickBack = () => {
    // 入力内容を Redux に保存（ChangeTime から戻ってきたときに復元するため）
    dispatch(saveFormAnswers({ answerList, numOfAttendees, fromChangeForm: true, note }));
    // モード1は個人情報・送信方法も保存
    if (businessChangeMode === 1) {
      dispatch(saveUserInfo({ userName: name, address, birthday, telNum, mailAddress, baseName }));
      dispatch(saveSendType({ sendType }));
    }
    navigate('/change/time', {
      state: { date: newDate, dayOfWeek: newDayOfWeek, sameDay, sameTime },
    });
  };

  // フォーム項目の描画（showFullForm 時は個人情報フィールドも含めて描画）
  const createForm = (data) => {
    if (!answerList || answerList.length === 0) return null;
    const { input_info: inputInfo = [] } = data;
    const formTable = [];

    for (let i = 0; i < inputInfo.length; i += 1) {
      const {
        input_type: inputType = 1,
        title,
        explanation = '',
        require = true,
        placeholder = '',
        selection = [],
        display = true,
        url = '',
        urlText = '',
      } = inputInfo[i];
      const { answer = [] } = answerList[i] || {};

      // showFullForm でない場合は個人情報フィールドをスキップ（モード1用件変更なし・モード2・3の動作を維持）
      if (!showFullForm && PERSONAL_INFO_TYPES.includes(inputType)) continue;

      // テキスト（showFullForm 時は氏名・住所も MakeText で表示）
      if (
        inputType === INPUT_TYPE_TEXT ||
        inputType === INPUT_TYPE_NAME ||
        inputType === INPUT_TYPE_ADDRESS
      ) {
        if (display) {
          formTable.push(
            <MakeText
              question={title}
              inputType={inputType}
              explanation={explanation}
              need={require}
              placeholder={placeholder}
              answer={answer}
              setter={changeSetter}
              num={i}
              key={`text_${i}`}
            />
          );
        }
      }

      // テキストエリア
      if (inputType === INPUT_TEXTAREA_FLAG) {
        formTable.push(
          <MakeTextArea
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            placeholder={placeholder}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`textArea_${i}`}
          />
        );
      }

      // 日付・生年月日（showFullForm 時は生年月日も MakeDate で表示）
      if (inputType === INPUT_DATE_FLAG || inputType === INPUT_TYPE_BIRTHDAY) {
        if (display) {
          formTable.push(
            <MakeDate
              question={title}
              inputType={inputType}
              explanation={explanation}
              need={require}
              answer={answer}
              setter={changeSetter}
              num={i}
              key={`date_${i}`}
            />
          );
        }
      }

      // 電話番号（showFullForm 時は TelNumBox + SMS転記ボタンを表示）
      if (inputType === INPUT_TELLNUM_FLAG || inputType === INPUT_TYPE_PHONE) {
        if (display) {
          formTable.push(
            <React.Fragment key={`tellNum_${i}`}>
              <TelNumBox
                question={title}
                inputType={inputType}
                explanation={explanation}
                need={require}
                placeholder={placeholder}
                answer={answer}
                setter={changeSetter}
                num={i}
              />
              {showFullForm && smsEnable === true ? (
                <a
                  role="button"
                  className="tellNumPosting"
                  onClick={() => {
                    setTelNum(answer[0]);
                  }}
                  tabIndex={0}
                >
                  電話番号を転記
                </a>
              ) : null}
            </React.Fragment>
          );
        }
      }

      // 説明（入力なし）
      if (inputType === INPUT_EXP_FLAG) {
        if (!answerList[i]) {
          changeSetter(
            {
              question: title,
              inputType: INPUT_EXP_FLAG,
              answer: ['説明のため回答はありません。'],
            },
            i
          );
        }
        formTable.push(
          <div className="mb20 txtCenter" key={`exp_${i}`}>
            <span>{explanation}</span>
          </div>
        );
      }

      // ラジオボタン
      if (inputType === INPUT_RADIO_FLAG) {
        formTable.push(
          <MakeRadioButton
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            selection={selection}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`radioButton_${i}`}
          />
        );
      }

      // チェックボックス
      if (inputType === INPUT_CHECK_FLAG) {
        formTable.push(
          <MakeCheckBox
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            selection={selection}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`checkBox_${i}`}
          />
        );
      }

      // セレクトボックス
      if (inputType === INPUT_SELECT_FLAG) {
        formTable.push(
          <MakeSelectBox
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            placeholder={placeholder}
            selection={selection}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`selectBox_${i}`}
          />
        );
      }

      // 数字のみ
      if (inputType === INPUT_MATHONLY_FLAG) {
        formTable.push(
          <MakeInt
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            placeholder={placeholder}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`mathOnly_${i}`}
          />
        );
      }

      // 英数字
      if (inputType === INPUT_ENGMATH_FLAG) {
        formTable.push(
          <MakeIntEng
            question={title}
            inputType={inputType}
            explanation={explanation}
            need={require}
            placeholder={placeholder}
            answer={answer}
            setter={changeSetter}
            num={i}
            key={`engAndMath_${i}`}
          />
        );
      }

      // 外部URL
      if (inputType === INPUT_URL_FLAG) {
        if (!answerList[i]) {
          changeSetter(
            {
              question: title,
              inputType: INPUT_URL_FLAG,
              answer: ['外部URLのため、回答はありません。'],
            },
            i
          );
        }
        formTable.push(
          <MakeUrlItem
            question={title}
            explanation={explanation}
            url={url}
            urlText={urlText}
            key={`url_${i}`}
          />
        );
      }
    }

    const newFormTable = formTable.flatMap((item, index) =>
      index === formTable.length - 1 ? [item] : [item, <br key={`br-${index}`} />]
    );

    return newFormTable;
  };

  // ページタイトルのsetState
  const getPageTitle = (data) => {
    const { form_title: formTitle = '' } = data;
    return formTitle;
  };

  const getTitleExp = (data) => {
    const { form_explanation: formExplanation = '' } = data;

    return formExplanation;
  };

  return (
    <>
      <ReserveHeader servCd="reserve" lgCd={lgCd} />
      <div id="container">
        <TopicPath />
        <main>
          <article>
            <ChangeSelectionSummary />
            <div id="contents" className="customForm">
              <MoveTop lgCd={lgCd} />
              <div className="inner">
                <h2>{pageName}</h2>
                <div className="txtCenter" style={{ fontWeight: 'bold' }}>
                  {formSetting === '' ? null : getPageTitle(formSetting)}
                </div>
                <p className="formExpStyle">
                  {formSetting === '' ? null : getTitleExp(formSetting)}
                </p>
                {formSetting !== '' && createForm(formSetting)}
              </div>
              <div className="inner">
                {maxAttendees > 0 && rsvFlag === 0 ? (
                  <dl className="custom">
                    <dt>同席者数</dt>
                    <span className="mb20">
                      予約者ご本人様以外に参加者がいる場合は人数を選択してください。
                    </span>
                    <dd>
                      <select
                        name="sankaninzuu"
                        value={numOfAttendees}
                        onChange={(e) => setNumOfAttendees(e.target.value)}
                      >
                        {numOfAttendeesOptions}
                      </select>
                    </dd>
                  </dl>
                ) : null}
              </div>
              <div className="inner">
                <dl className="custom">
                  <dt>備考</dt>
                  <dd>
                    <textarea
                      placeholder={remarksColumnPlaceholder}
                      defaultValue={note}
                      onBlur={(e) => {
                        setNote(e.target.value);
                      }}
                    />
                  </dd>
                </dl>
              </div>

              {/* showFullForm 時のみ: SMS/メールアドレス連絡方法セクション（CustomizeReserveForm.jsx と同等） */}
              {showFullForm && (
                <div className="inner">
                  {smsEnable === true ? (
                    <dl className="custom">
                      <dt className="mb10">予約完了のご案内</dt>
                      <span>
                        WEB窓口を利用する予定の環境（PC、スマホなど）で受信いただくことを推奨します。
                      </span>
                      <dd>
                        <div className="sendtypeContainer">
                          <label htmlFor="mail" className="sendTypeLabel">
                            <input
                              id="mail"
                              type="radio"
                              name="sendType"
                              value={1}
                              onChange={(e) => setSendType(parseInt(e.target.value, 10))}
                              checked={sendType === 1}
                            />
                            メール
                          </label>
                          <label htmlFor="sms" className="sendTypeLabel">
                            <input
                              id="sms"
                              type="radio"
                              name="sendType"
                              value={2}
                              onChange={(e) => setSendType(parseInt(e.target.value, 10))}
                              checked={sendType === 2}
                            />
                            SMS
                          </label>
                          <label htmlFor="mail&sms" className="sendTypeLabel">
                            <input
                              id="mail&sms"
                              type="radio"
                              name="sendType"
                              value={3}
                              onChange={(e) => setSendType(parseInt(e.target.value, 10))}
                              checked={sendType === 3}
                            />
                            メール+SMS
                          </label>
                        </div>
                      </dd>
                    </dl>
                  ) : null}
                  <div className="mb20">
                    {sendType === 1 ? (
                      <span>
                        予約完了時にメールを送信します。
                        <br />
                        {fromMailAddress}からの受信が可能なメールを入力してください。
                      </span>
                    ) : null}
                    {sendType === 2 ? (
                      <span>
                        予約完了時にSMSを送信します。
                        <br />
                        SMSを利用する場合は電話番号に携帯電話の番号を入力してください。
                      </span>
                    ) : null}
                    {sendType === 3 ? (
                      <span>
                        予約完了時にメールとSMSを送信します。
                        <br />
                        SMSを利用する場合は電話番号に携帯電話の番号を入力してください。
                        <br />
                        {fromMailAddress}からの受信が可能なメールを入力してください。
                      </span>
                    ) : null}
                  </div>
                  {sendType === 1 ? (
                    <dl className="custom">
                      <dt>
                        メールアドレス&nbsp;<span className="required">*必須</span>
                      </dt>
                      <dd>
                        <input
                          type="email"
                          name="email"
                          defaultValue={mailAddress}
                          onChange={(e) => setMailAddress(e.target.value)}
                        />
                      </dd>
                      <dt>
                        メールアドレス&nbsp;<span>（確認用）</span>
                      </dt>
                      <dd>
                        <input
                          type="email"
                          name="email2"
                          defaultValue={confMailAddress}
                          onChange={(e) => setConfMailAddress(e.target.value)}
                          onPaste={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          autoComplete="off"
                        />
                      </dd>
                    </dl>
                  ) : null}
                  {sendType === 2 ? (
                    <dl className="custom">
                      <dt>
                        電話番号（SMS送信用）&nbsp;<span className="required">*必須</span>
                      </dt>
                      <dd>
                        <input
                          type="tel"
                          name="telNum"
                          value={telNum}
                          onChange={(e) => setTelNum(e.target.value)}
                        />
                      </dd>
                    </dl>
                  ) : null}
                  {sendType === 3 ? (
                    <dl className="custom">
                      <dt>
                        メール&nbsp;<span className="required">*必須</span>
                      </dt>
                      <dd>
                        <input
                          type="email"
                          name="email"
                          defaultValue={mailAddress}
                          onChange={(e) => setMailAddress(e.target.value)}
                        />
                      </dd>
                      <dt>
                        メール&nbsp;<span>（確認用）</span>
                      </dt>
                      <dd>
                        <input
                          type="email"
                          name="email2"
                          defaultValue={confMailAddress}
                          onChange={(e) => setConfMailAddress(e.target.value)}
                          onPaste={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          autoComplete="off"
                        />
                      </dd>
                      <dt>
                        電話番号（SMS送信用）&nbsp;<span className="required">*必須</span>
                      </dt>
                      <dd>
                        <input
                          type="tel"
                          name="telNum"
                          value={telNum}
                          onChange={(e) => setTelNum(e.target.value)}
                        />
                      </dd>
                    </dl>
                  ) : null}
                </div>
              )}

              <div className="inner">
                <ul id="navi" className="row jcSB">
                  <li className="col2">
                    <a role="button" onClick={onClickBack} className="btn btBack" tabIndex={0}>
                      前に戻る
                    </a>
                  </li>
                  <li className="col2 mr0 row jcFE">
                    <a role="button" className="btn btNext" onClick={onClickNext} tabIndex={0}>
                      入力内容を確定し次へ
                    </a>
                  </li>
                </ul>
              </div>
              <div className="cancelAnnounce">{CANCEL_CHANGE_ANNOUNCE}</div>
            </div>
          </article>
        </main>
      </div>
      <Footer />
    </>
  );
};

export default ChangeForm;

// フォーム項目コンポーネントは CustomizeReserveForm.jsx から import して使用
